import { ref } from 'vue';
import directus from './useDirectus.js';
import { useCollectionsStore } from '../store/collections.js';

const SPACES_FIELDS = [
  'id',
  'slug',
  'name',
  'description',
  'icon',
  'color',
  'status',
  'sort',
  'date_created',
  'date_updated',
  'user_created',
  'members.id',
  'members.role',
  'members.directus_users_id.id',
  'members.directus_users_id.first_name',
  'members.directus_users_id.last_name',
  'members.directus_users_id.email',
  'members.directus_users_id.avatar'
];

/**
 * Fetch all Spaces visible to the current user, with stale-while-revalidate caching.
 * Returns a reactive ref; populated on first load.
 */
export function useSpaces() {
  const spaces = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const cache = useCollectionsStore();

  async function load({ forceRefresh = false, includeDrafts = false } = {}) {
    loading.value = true;
    error.value = null;
    try {
      const filter = includeDrafts
        ? { status: { _neq: 'archived' } }
        : { status: { _eq: 'published' } };
      const { data } = await cache.fetch(
        `orbit_spaces${includeDrafts ? '_all' : ''}`,
        async () => {
          const result = await directus.items('orbit_spaces').readByQuery({
            fields: SPACES_FIELDS,
            filter,
            sort: ['sort', 'name'],
            limit: -1
          });
          return result.data || [];
        },
        { forceRefresh }
      );
      spaces.value = data;
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
    return spaces.value;
  }

  async function getBySlug(slug, { forceRefresh = false } = {}) {
    // Always include drafts — the Directus read permission decides what the
    // user can actually see (published OR member-of). Without this the
    // frontend pre-filters to "published only" and editors lose access to
    // their own draft Spaces.
    if (!spaces.value.length || forceRefresh) {
      await load({ forceRefresh, includeDrafts: true });
    }
    return spaces.value.find(s => s.slug === slug) || null;
  }

  async function getById(id, { forceRefresh = false } = {}) {
    if (!spaces.value.length || forceRefresh) {
      await load({ forceRefresh, includeDrafts: true });
    }
    return spaces.value.find(s => s.id === Number(id)) || null;
  }

  async function updateSpace(id, patch) {
    const updated = await directus.items('orbit_spaces').updateOne(id, patch);
    cache.invalidate('orbit_spaces');
    cache.invalidate('orbit_spaces_all');
    return updated;
  }

  return {
    spaces,
    loading,
    error,
    load,
    getBySlug,
    getById,
    updateSpace
  };
}
